import { timeoutAfter } from '../timeoutAfter';
import { throttleAndWaitPrev } from './throttleAndWaitPrev';

type HandlerOption = {
  noHistory?: boolean;
  timeout?: number;
};

/**
 * `location.href`를 주어진 `href`로 바꾸는 이벤트 처리기를 반환한다.
 * 처리기에는 1초 쓰로틀링 및 `preventDefault()`를 적용한다.
 *
 * 단, 사용자가 컨트롤 키를 누른 상태라면 새 창으로 열려야 하므로 위의 항목은 모두 무시한다.
 *
 * @param href 이동할 주소.
 * @param beforeExecute 처리기 호출 전에 우선 실행할 콜백 함수. 주로 사용자 추적용.
 * @param option.noHistory 히스토리 적재를 원하지 않는 경우로 location.href 가 아닌 location.replace 로 적용.
 * @param option.timeout `beforeExecute()`의 처리를 기다릴 최대 대기시간(ms). 컨트롤 키를 누른 상태라면 무시함.
 */
export const linkClickHandler = (
  href?: string,
  beforeExecute?: (event: MouseEvent | React.MouseEvent) => void | Promise<void>,
  option?: HandlerOption,
): ((e: MouseEvent | React.MouseEvent) => void | Promise<void>) => {
  const noHistory = option?.noHistory ?? false;
  const timeout = option?.timeout ?? 250;

  const navigate = (): void | Promise<void> => {
    href && (noHistory ? window.location.replace(href) : (window.location.href = href));
  };

  // befExec vs 250ms
  const beforeExecuteRaced = (event: MouseEvent | React.MouseEvent): void | Promise<void> =>
    beforeExecute && timeoutAfter(timeout)(beforeExecute(event));

  const throttledNavigate = throttleAndWaitPrev(navigate, beforeExecuteRaced);

  return (event) => {
    if (event.ctrlKey || event.metaKey) {
      beforeExecute?.(event);
      return;
    }

    event.preventDefault();
    return throttledNavigate(event);
  };
};
